import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Gift, Mail, ChevronRight, ShoppingBag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSeo } from '@/lib/seo';
import { useBookingExperiences } from '@/lib/useBookingExperiences';
import { useCart } from '@/lib/CartContext';

const PRESETS = [25, 50, 100, 150, 250];
const MIN = 10;
const MAX = 1000;

const inputCls = "bg-white/5 border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 focus:bg-white/8 transition-all duration-200 rounded-none";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-7 h-7 bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-3.5 h-3.5 text-primary" />
    </div>
    <p className="text-white/70 text-[10px] font-bold tracking-[0.15em]">{label}</p>
    <div className="flex-1 h-px bg-white/5" />
  </div>
);

export default function GiftCards() {
  useSeo({
    title: 'Gift Cards — Motion Reality Sim Racing',
    description: 'Buy a Motion Reality gift card. Redeemable on sim racing hardware and booking sessions. Email or postal delivery.',
  });

  const navigate = useNavigate();
  const { addGiftCardToCart, totalItems } = useCart();

  const [step, setStep] = useState('details');
  const [preset, setPreset] = useState(50);
  const [useCustom, setUseCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [sendTo, setSendTo] = useState('other');
  const [form, setForm] = useState({
    recipientName: '', recipientEmail: '',
    recipientAddressLine1: '', recipientAddressLine2: '', recipientCity: '', recipientPostcode: '',
    message: '',
  });
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [addedSummary, setAddedSummary] = useState(null);
  const [mode, setMode] = useState('monetary');
  const [selectedExperience, setSelectedExperience] = useState(null);

  const experiences = useBookingExperiences();
  const selectedExp = experiences.find(e => e.key === selectedExperience);
  const finalAmount = mode === 'experience' ? (selectedExp ? Number(selectedExp.price) : 0) : (useCustom ? (parseFloat(customAmount) || 0) : preset);

  const handleAddToBasket = async (e) => {
    e.preventDefault();
    if (mode === 'experience' && !selectedExp) { setError('Please select an experience.'); return; }
    if (mode === 'monetary' && (finalAmount < MIN || finalAmount > MAX)) { setError(`Amount must be between £${MIN} and £${MAX}.`); return; }
    if (sendTo === 'other' && !form.recipientName) { setError('Please enter the recipient name.'); return; }
    if (deliveryMethod === 'email' && sendTo === 'other' && !form.recipientEmail) { setError('Please enter the recipient email.'); return; }
    if (deliveryMethod === 'post' && (!form.recipientAddressLine1 || !form.recipientCity || !form.recipientPostcode)) { setError('Please complete the recipient postal address.'); return; }
    setError('');
    setAdding(true);

    const productName = mode === 'experience'
      ? (selectedExp ? `${selectedExp.name} Gift Card` : 'Experience Gift Card')
      : `Gift Card £${finalAmount.toFixed(2)}`;

    try {
      await addGiftCardToCart({
        mode,
        experienceType: mode === 'experience' ? selectedExperience : '',
        deliveryMethod,
        sendToSelf: sendTo === 'self',
        recipientName: form.recipientName,
        recipientEmail: form.recipientEmail,
        recipientAddressLine1: form.recipientAddressLine1,
        recipientAddressLine2: form.recipientAddressLine2,
        recipientCity: form.recipientCity,
        recipientPostcode: form.recipientPostcode,
        giftMessage: form.message,
        productName,
        price: finalAmount,
      });
      setAddedSummary({ productName, amount: finalAmount, deliveryMethod, recipient: sendTo === 'self' ? 'Yourself' : (form.recipientName || 'Recipient') });
      setStep('success');
    } catch (err) {
      setError('Could not add to basket. Please try again.');
    }
    setAdding(false);
  };

  const resetForm = () => {
    setMode('monetary');
    setPreset(50);
    setUseCustom(false);
    setCustomAmount('');
    setSelectedExperience(null);
    setDeliveryMethod('email');
    setSendTo('other');
    setForm({ recipientName: '', recipientEmail: '', recipientAddressLine1: '', recipientAddressLine2: '', recipientCity: '', recipientPostcode: '', message: '' });
    setStep('details');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-16">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-[var(--header-offset)] z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          {step !== 'success' ? (
            <Link to="/" className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/70 text-xs font-medium transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
            </Link>
          ) : <div />}
          <div className="flex items-center gap-2 text-white/20">
            <ShoppingBag className="w-3 h-3" />
            <span className="text-[10px] font-bold tracking-wider hidden sm:block">{totalItems > 0 ? `${totalItems} IN BASKET` : 'EMPTY BASKET'}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="mb-10">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">GIFT CARDS</p>
          <h1 className="text-white font-black text-3xl md:text-5xl uppercase leading-tight">
            Give The Gift<br />Of Speed.
          </h1>
          <p className="text-white/40 mt-4 max-w-lg text-sm">
            Redeemable on sim racing hardware and booking sessions. Add to your basket and check out in one go — alongside hardware and sessions.
          </p>
        </div>

        <AnimatePresence>
          {step === 'success' && addedSummary && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-lg mx-auto text-center py-16"
            >
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                <div className="relative w-24 h-24 bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-white" />
                </div>
              </div>
              <p className="text-primary text-[10px] font-bold tracking-[0.2em] mb-3">ADDED TO BASKET</p>
              <h2 className="text-white font-black text-3xl uppercase mb-4">{addedSummary.productName}</h2>
              <div className="bg-white/[0.03] border border-white/10 p-6 mb-8 text-left">
                <div className="flex justify-between text-sm mb-2"><span className="text-white/30 text-xs">Value</span><span className="text-white font-bold">£{addedSummary.amount.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm mb-2"><span className="text-white/30 text-xs">Delivery</span><span className="text-white/60 text-xs">{addedSummary.deliveryMethod === 'post' ? 'Postal gift card' : 'Email digital code'}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/30 text-xs">For</span><span className="text-white/60 text-xs">{addedSummary.recipient}</span></div>
              </div>
              <p className="text-white/40 text-sm mb-8">
                Your gift card code will be generated and emailed to you when you complete checkout.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/cart">
                  <button className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-8 py-3.5 transition-colors">
                    VIEW BASKET
                  </button>
                </Link>
                <button onClick={resetForm} className="text-white/30 hover:text-white/70 font-bold text-xs tracking-widest px-4 py-3.5 transition-colors">
                  + ADD ANOTHER
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {step === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <motion.form key="details" {...fadeUp} onSubmit={handleAddToBasket} className="space-y-8">
                {/* Amount */}
                <div className="bg-white/[0.02] border border-white/5 p-6">
                  <SectionHeader icon={Gift} label="CHOOSE GIFT CARD" />
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <button type="button" onClick={() => setMode('monetary')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${mode === 'monetary' ? 'bg-primary text-white' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      MONETARY
                    </button>
                    <button type="button" onClick={() => setMode('experience')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${mode === 'experience' ? 'bg-primary text-white' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      EXPERIENCE
                    </button>
                  </div>

                  {mode === 'monetary' && (
                    <>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-5">
                        {PRESETS.map(v => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => { setPreset(v); setUseCustom(false); }}
                            className={`py-4 text-center font-bold text-sm transition-all ${
                              !useCustom && preset === v
                                ? 'bg-primary text-white'
                                : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'
                            }`}
                          >
                            £{v}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setUseCustom(true)}
                        className={`w-full py-4 text-center font-bold text-sm transition-all ${
                          useCustom ? 'bg-primary text-white' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'
                        }`}
                      >
                        CUSTOM AMOUNT
                      </button>
                      {useCustom && (
                        <div className="mt-3">
                          <Input
                            type="number"
                            min={MIN}
                            max={MAX}
                            step="1"
                            value={customAmount}
                            onChange={e => setCustomAmount(e.target.value)}
                            className={inputCls}
                            placeholder={`£${MIN} – £${MAX}`}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {mode === 'experience' && (
                    <div className="space-y-3">
                      {experiences.length === 0 && (
                        <p className="text-white/30 text-xs py-6 text-center">No bookable experiences available right now.</p>
                      )}
                      {experiences.map(exp => (
                        <button
                          key={exp.key}
                          type="button"
                          onClick={() => setSelectedExperience(exp.key)}
                          className={`w-full text-left p-4 border transition-all ${
                            selectedExperience === exp.key
                              ? 'bg-primary/10 border-primary'
                              : 'bg-primary/5 border-primary/30 hover:border-primary hover:bg-primary/10'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-bold text-sm">{exp.name}</p>
                              {exp.tag && <p className="text-white/30 text-[10px] tracking-widest mt-0.5">{exp.tag}</p>}
                            </div>
                            <p className="text-primary font-black text-lg">£{Number(exp.price).toFixed(2)}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  <p className="text-white/30 text-[10px] mt-3">
                    {mode === 'experience' ? 'Experience voucher — ' : 'Selected value: '}
                    <span className="text-primary font-bold">£{finalAmount.toFixed(2)}</span>
                    {mode === 'experience' && selectedExp ? ` · ${selectedExp.name}` : ''}
                  </p>
                </div>

                {/* Delivery & recipient */}
                <div className="bg-white/[0.02] border border-white/5 p-6">
                  <SectionHeader icon={Mail} label="DELIVERY & RECIPIENT" />
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <button type="button" onClick={() => setDeliveryMethod('email')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${deliveryMethod === 'email' ? 'bg-primary text-white' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      EMAIL DIGITAL CODE
                    </button>
                    <button type="button" onClick={() => setDeliveryMethod('post')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${deliveryMethod === 'post' ? 'bg-primary text-white' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      POSTAL GIFT CARD
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <button type="button" onClick={() => setSendTo('self')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${sendTo === 'self' ? 'bg-primary text-white border-primary' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      FOR MYSELF
                    </button>
                    <button type="button" onClick={() => setSendTo('other')}
                      className={`py-3.5 text-xs font-bold tracking-wider transition-all ${sendTo === 'other' ? 'bg-primary text-white border-primary' : 'bg-primary/10 border border-primary/40 text-white hover:bg-primary hover:border-primary'}`}>
                      AS A GIFT
                    </button>
                  </div>

                  {sendTo === 'other' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className={labelCls}>RECIPIENT NAME *</Label>
                        <Input value={form.recipientName} onChange={e => setForm({ ...form, recipientName: e.target.value })} className={inputCls} placeholder="Recipient name" />
                      </div>
                      {deliveryMethod === 'email' && (
                        <div>
                          <Label className={labelCls}>RECIPIENT EMAIL *</Label>
                          <Input type="email" value={form.recipientEmail} onChange={e => setForm({ ...form, recipientEmail: e.target.value })} className={inputCls} placeholder="recipient@email.com" />
                        </div>
                      )}
                    </div>
                  )}

                  {deliveryMethod === 'post' && (
                    <div className="grid grid-cols-1 gap-4 mt-4">
                      <div>
                        <Label className={labelCls}>ADDRESS LINE 1 *</Label>
                        <Input value={form.recipientAddressLine1} onChange={e => setForm({ ...form, recipientAddressLine1: e.target.value })} className={inputCls} placeholder="Street address" />
                      </div>
                      <div>
                        <Label className={labelCls}>ADDRESS LINE 2</Label>
                        <Input value={form.recipientAddressLine2} onChange={e => setForm({ ...form, recipientAddressLine2: e.target.value })} className={inputCls} placeholder="Optional" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className={labelCls}>CITY *</Label>
                          <Input value={form.recipientCity} onChange={e => setForm({ ...form, recipientCity: e.target.value })} className={inputCls} placeholder="City" />
                        </div>
                        <div>
                          <Label className={labelCls}>POSTCODE *</Label>
                          <Input value={form.recipientPostcode} onChange={e => setForm({ ...form, recipientPostcode: e.target.value })} className={inputCls} placeholder="Postcode" />
                        </div>
                      </div>
                    </div>
                  )}

                  {sendTo === 'other' && (
                    <div className="mt-4">
                      <Label className={labelCls}>GIFT MESSAGE (OPTIONAL)</Label>
                      <textarea
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        maxLength={300}
                        rows={3}
                        className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 placeholder:text-white/20 focus:border-primary/60 outline-none resize-none"
                        placeholder="Add a personal note..."
                      />
                    </div>
                  )}
                </div>

                <AnimatePresence>
                  {error && (
                    <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3">{error}</motion.p>
                  )}
                </AnimatePresence>

                <motion.button type="submit" disabled={adding} whileHover={!adding ? { scale: 1.01 } : {}} whileTap={!adding ? { scale: 0.99 } : {}}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest py-4 transition-all flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                  {adding ? 'ADDING...' : <>ADD TO BASKET <ChevronRight className="w-4 h-4" /></>}
                </motion.button>
              </motion.form>
            </div>

            {/* Right info panel */}
            <div className="lg:col-span-2">
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
                className="bg-white/[0.02] border border-white/5 p-6 sticky top-[7.5rem]">
                <p className="text-white/40 text-[10px] font-bold tracking-[0.18em] mb-5">HOW IT WORKS</p>
                <div className="space-y-4 text-sm">
                  <div className="flex gap-3">
                    <span className="text-primary font-black text-lg leading-none">1</span>
                    <p className="text-white/60 text-xs leading-relaxed">Choose an amount or experience, and whether it's for you or a gift — digital code by email or a physical gift card by post.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-primary font-black text-lg leading-none">2</span>
                    <p className="text-white/60 text-xs leading-relaxed">Add it to your basket. Checkout once for hardware, sessions and gift cards together.</p>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-primary font-black text-lg leading-none">3</span>
                    <p className="text-white/60 text-xs leading-relaxed">We generate your gift card code on payment and email it to you. Use it at checkout on the store or booking page.</p>
                  </div>
                </div>
                <div className="border-t border-white/5 mt-6 pt-5">
                  <p className="text-white/20 text-[10px] leading-relaxed">
                    Gift cards never expire. Redeemable on hardware orders and booking sessions. Terms apply.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}